/**
 * What the AR wall preview needs to know about the piece being bought, and
 * the link that carries it to a phone.
 *
 * Pure and DOM-free: the editor imports it eagerly (it is tiny), and the
 * three.js side never sees a variant — only these numbers.
 */
import { isUsableCrop, parseArParams, parseColour, type NormalisedCrop } from "./ar-maths";

/**
 * The sides of the print:
 *  - "photo": a stretched canvas, whose sides are the photo's own edge pulled
 *    round the frame — shown as the average colour of that edge;
 *  - "white": paper and rolled canvas, flat, white at the edge;
 *  - a `#rrggbb` colour: a solid-colour edge chosen in the editor (or a frame
 *    colour on the test page).
 */
export type ArEdge = "photo" | "white" | `#${string}`;

export interface ArProduct {
  widthCm: number;
  heightCm: number;
  /** Stretcher depth; 0 is a flat print. */
  depthCm: number;
  edge: ArEdge;
}

export interface ArVariantInput {
  /** Variant axis values, e.g. material "canvas" | "paper". */
  material?: string | null;
  /** "stretched" (on a frame) | "rolled" (sent in a tube), or absent. */
  mount?: string | null;
  /** The visible face, already turned to the chosen orientation. */
  trimWidthMm: number;
  trimHeightMm: number;
  /** Wrap round the stretcher per side; 20–25 mm on a stretched canvas. */
  bleedMm?: number | null;
  /** The design's edge treatment, when the editor has one. */
  edgeMode?: string | null;
  edgeColor?: string | null;
}

/** Stretcher bars come 2 cm deep in the shop's range; nothing ships deeper than 4. */
export const MIN_STRETCHER_CM = 2;
export const MAX_STRETCHER_CM = 4;

const roundTenth = (n: number) => Math.round(n * 10) / 10;

/**
 * Variant → AR parameters.
 *
 * Only a stretched piece has depth: the wrap folds round the bars, so the
 * bleed (per side) is the bars' depth, rounded to half a centimetre and kept
 * within the 2–4 cm the shop makes. Paper and rolled canvas hang flat.
 */
export function arParamsForVariant(input: ArVariantInput): ArProduct {
  const widthCm = roundTenth(Math.max(0, input.trimWidthMm) / 10);
  const heightCm = roundTenth(Math.max(0, input.trimHeightMm) / 10);
  const stretched = input.mount === "stretched";

  let depthCm = 0;
  if (stretched) {
    const bleed = Number(input.bleedMm);
    const fromBleed = Number.isFinite(bleed) && bleed >= 10 ? Math.round(bleed / 5) / 2 : MIN_STRETCHER_CM;
    depthCm = Math.min(MAX_STRETCHER_CM, Math.max(MIN_STRETCHER_CM, fromBleed));
  }

  const solid = input.edgeMode === "color" ? parseColour(input.edgeColor ?? null) : null;
  const edge: ArEdge = stretched ? ((solid as ArEdge | null) ?? "photo") : "white";

  return { widthCm, heightCm, depthCm, edge };
}

/**
 * Which kind of piece is on the wall. Decides the words (a map is not an
 * "enlargement") and where the picture comes from:
 *  - "canvas": the customer's own photo from the povečave editor;
 *  - "art" / "photography": a catalogue piece, whose image is ours and public;
 *  - "map": the map editor's server-drawn composite, which a link redraws
 *    from the design it carries (ar-map-link.ts).
 */
export type ArProductKind = "canvas" | "art" | "photography" | "map";
export const AR_PRODUCT_KINDS: readonly ArProductKind[] = ["canvas", "art", "photography", "map"];

/** The `p` link parameter; anything unknown (or absent, the old links) is a canvas. */
export function parseArKind(raw: string | null | undefined): ArProductKind {
  return AR_PRODUCT_KINDS.includes(raw as ArProductKind) ? (raw as ArProductKind) : "canvas";
}

/**
 * The arPreview texts that name the piece ("your enlargement", "hang it"),
 * and so need their own wording — and grammatical gender — per kind. The
 * canvas keeps the original keys; the others add `_<kind>`.
 */
const NAMES_THE_PIECE = new Set([
  "title",
  "alt",
  "viewerLabel",
  "preparing",
  "desktop",
  "qrAlt",
  "androidUnsupported",
  "viewOnWall",
  "realSize",
  "guideTitle",
  "iosSafari",
]);

/** The locale key for an arPreview text, worded for this kind of piece. */
export function arCopyKey(kind: ArProductKind, key: string): string {
  return kind === "canvas" || !NAMES_THE_PIECE.has(key) ? `arPreview.${key}` : `arPreview.${key}_${kind}`;
}

/**
 * "Umetnina · 60 × 40 cm · Platno · Napeto na okvir". The povečave editor
 * already says what it is, so a canvas line starts at the size; the other
 * kinds name the piece first (`arPreview.product_<kind>`).
 */
export function arSizeLine(parts: { productName?: string | null; size: string; finish?: Array<string | null | false | undefined> }): string {
  return [parts.productName, parts.size, ...(parts.finish ?? [])].filter(Boolean).join(" · ");
}

/**
 * A catalogue piece (art, professional photography) as AR parameters.
 *
 * The server prints it whole with the canvas product's mirrored wrap
 * (artwork-order.service: crop 0,0,1,1, edge "mirror"), so a stretched piece
 * gets photo-coloured sides; the size is the one the page offered, already
 * turned to the piece's orientation.
 */
export function artworkArProduct(input: {
  material?: string | null;
  mount?: string | null;
  widthMm: number;
  heightMm: number;
  bleedMm?: number | null;
}): ArProduct {
  return arParamsForVariant({
    material: input.material,
    mount: input.mount,
    trimWidthMm: input.widthMm,
    trimHeightMm: input.heightMm,
    bleedMm: input.bleedMm,
    edgeMode: "mirror",
  });
}

/** What a link to the AR view carries. */
export interface ArLink extends ArProduct {
  /** Normalised crop of the photo's FACE (not the wrap), or null for a centre crop. */
  crop: NormalisedCrop | null;
  /** Variant axes, so the phone can name the finish. */
  material: string | null;
  mount: string | null;
  /** The photo, unvalidated — the page must pass it through resolveImageUrl. */
  img: string | null;
  kind: ArProductKind;
  /** A catalogue piece's slug (art / photography); its image is loaded by slug. */
  art: string | null;
}

/** Catalogue slugs: letters, digits and dashes. Anything else is not one of ours. */
const ART_SLUG = /^[a-z0-9](?:[a-z0-9-]{0,118}[a-z0-9])?$/i;

export function isArtSlug(raw: string | null | undefined): raw is string {
  return !!raw && ART_SLUG.test(raw);
}

const AXIS_VALUE = /^[a-z0-9_-]{1,32}$/i;
const fixed = (n: number) => String(Math.round(n * 1e4) / 1e4);

/** The piece's size, depth, edge and finish as link parameters (every kind's link starts with these). */
export function productQuery(product: ArProduct, material?: string | null, mount?: string | null): URLSearchParams {
  const q = new URLSearchParams();
  q.set("w", fixed(product.widthCm));
  q.set("h", fixed(product.heightCm));
  q.set("depth", fixed(product.depthCm));
  q.set("edge", product.edge.replace(/^#/, ""));
  if (material && AXIS_VALUE.test(material)) q.set("m", material);
  if (mount && AXIS_VALUE.test(mount)) q.set("mt", mount);
  return q;
}

/**
 * The AR link for a catalogue piece (art, professional photography).
 *
 * Permanent, unlike the povečave link: the picture is the shop's own and
 * public (GET /artwork/:slug/image), so the link names the piece by its slug
 * and the phone loads the image itself — no signed URL, no session. The size,
 * depth, edge and finish travel as they do for a canvas, so the phone shows
 * exactly the variant chosen on the computer.
 */
export function arArtworkLink(opts: {
  origin: string;
  locale: string;
  kind: "art" | "photography";
  slug: string;
  product: ArProduct;
  material?: string | null;
  mount?: string | null;
}): string | null {
  if (!isArtSlug(opts.slug)) return null;
  const q = productQuery(opts.product, opts.material, opts.mount);
  q.set("p", opts.kind);
  q.set("art", opts.slug);
  return `${opts.origin}/${opts.locale}/ar-preview?${q.toString()}`;
}

/**
 * The URL that opens the same design in the AR view on another device
 * (the desktop QR code, the "open in browser" hand-off).
 *
 * Null when the photo only exists on this device (a `blob:`/`data:` URL):
 * a link without the photo would show somebody else's idea of the design.
 * The photo URL is a signed storage link that expires (24 h), so the link
 * is a short-lived one by nature.
 */
export function arDeepLink(opts: {
  origin: string;
  locale: string;
  product: ArProduct;
  crop: NormalisedCrop | null;
  imageUrl: string | null;
  material?: string | null;
  mount?: string | null;
}): string | null {
  const { origin, locale, product, crop, imageUrl } = opts;
  if (!imageUrl) return null;
  let img: URL;
  try {
    img = new URL(imageUrl, origin);
  } catch {
    return null;
  }
  if (img.protocol !== "https:" && img.origin !== origin) return null;

  const q = productQuery(product, opts.material, opts.mount);
  if (isUsableCrop(crop)) q.set("crop", [crop.x, crop.y, crop.width, crop.height].map(fixed).join(","));
  // Same-origin images travel as a path; storage links as they are.
  q.set("img", img.origin === origin ? img.pathname + img.search : img.toString());
  return `${origin}/${locale}/ar-preview?${q.toString()}`;
}

function parseCrop(raw: string | null): NormalisedCrop | null {
  if (!raw) return null;
  const parts = raw.split(",").map((p) => Number(p));
  if (parts.length !== 4) return null;
  const [x, y, width, height] = parts;
  const crop = { x, y, width, height };
  return isUsableCrop(crop) ? crop : null;
}

/** Reads an AR link (see arDeepLink); also the test page's hand-written URLs. */
export function parseArLink(get: (key: string) => string | null): ArLink {
  const base = parseArParams(get);
  const rawEdge = get("edge");
  let edge: ArEdge;
  if (base.frame) edge = base.frame as ArEdge;
  else if (rawEdge === "photo" || rawEdge === "white") edge = rawEdge;
  else edge = (parseColour(rawEdge) as ArEdge | null) ?? (base.depthCm > 0 ? "photo" : "white");
  const axis = (k: string) => {
    const v = get(k);
    return v && AXIS_VALUE.test(v) ? v : null;
  };
  const kind = parseArKind(get("p"));
  const art = get("art");
  return {
    widthCm: base.widthCm,
    heightCm: base.heightCm,
    depthCm: base.depthCm,
    edge,
    crop: parseCrop(get("crop")),
    material: axis("m"),
    mount: axis("mt"),
    img: base.img,
    kind,
    // Only a catalogue piece is loaded by slug.
    art: (kind === "art" || kind === "photography") && isArtSlug(art) ? art : null,
  };
}

/** "60x90" — the size as an analytics parameter (no personal data). */
export function analyticsSize(p: Pick<ArProduct, "widthCm" | "heightCm">): string {
  return `${p.widthCm}x${p.heightCm}`;
}
